import { get, last } from "lodash";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { Experience, PureCustomValue, SkillImportance } from "PFTypes";
import React, { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SkillsFrameworkData } from "../../../skills_frameworks.types";
import { SelectedSkillsBox } from "./selected_skills_box";
import css from "./skills_section.module.scss";

type SkillsImportanceSubsectionProps = {
  skillsSectionName: keyof SkillsFrameworkData["skills"];
  importance: SkillImportance;
};

const DEFAULT_SKILL_EXPERIENCE = Experience.Intermediate;

export const SkillsImportanceSubsection = ({
  skillsSectionName,
  importance
}: SkillsImportanceSubsectionProps) => {
  const { customTypes } = useCustomTypes();
  const {
    control,
    formState: { errors }
  } = useFormContext<SkillsFrameworkData>();
  const { t } = useTranslation("skillsFrameworks", { keyPrefix: "edit" });
  const skills = useWatch<SkillsFrameworkData>({ name: "skills" }) as SkillsFrameworkData["skills"];
  const skillsCustomType = useMemo(() => customTypes.find((type) => type.name === "skills"), [customTypes]);

  const otherSkillSectionName: keyof SkillsFrameworkData["skills"] =
    skillsSectionName === "essential" ? "supporting" : "essential";

  return (
    <>
      <Controller
        name={`skills.${skillsSectionName}`}
        control={control}
        render={({ field }) => (
          <CustomValuesEditField
            qaIdPrefix={`skills-framework-${skillsSectionName}-skills-field`}
            kind="bordered"
            tip=""
            label={t(skillsSectionName)}
            customType={skillsCustomType!}
            values={field.value.map(({ skill }) => skill)}
            letCreate={false}
            errors={get(errors, field.name)?.message}
            filterOptions={(entries: PureCustomValue[]) =>
              entries.filter(({ id }) => !skills[otherSkillSectionName].find(({ skill }) => id === skill.id))
            }
            handleChange={(newSkills: PureCustomValue[]) => {
              const newAddedSkill = last(newSkills);
              const newSkillsFormData = [
                ...skills[skillsSectionName],
                {
                  skill: { id: newAddedSkill?.id, value: newAddedSkill?.text },
                  importance,
                  experience: DEFAULT_SKILL_EXPERIENCE
                }
              ];
              field.onChange(newSkillsFormData);
            }}
            forceUnlocked
            displayValues={<React.Fragment />}
            classes={{ root: css.skillsSelect }}
          />
        )}
      />
      <SelectedSkillsBox skillImportance={importance} />
    </>
  );
};
